import React, { ReactNode, useState } from 'react';
import { AdvancedMarker, AdvancedMarkerProps } from '@vis.gl/react-google-maps';
import { MapPin } from '../../MapPin';
import {
  CourierReturnStatusType,
  CourierVehicleType,
} from '@src/pages/Deliveries/context/couriers';
import MapCard from '../../MapCard';
import { globalStyles } from 'snacks-design-system';
import { mapPinColorGenerator } from '../../MapPin/helpers';
import PIEText from '@src/shared/components/PIEText';
import DashboardVehicleSelector from '@src/pages/DeliveryDashboard/components/DashboardVehicleSelector';
import { useTranslation } from 'react-i18next';
import styles from '../CourierMapMarker/CourierMapMarker.module.scss';
import useEtaBack from '@src/pages/DeliveryDashboard/hooks/use-eta-back';
import useDashboardComputedFunctions from '@src/pages/DeliveryDashboard/hooks/use-dashboard-computed';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import dayjs from 'dayjs';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import useRenderPhase from '@src/shared/hooks/use-render-phase';

interface MockOrderMapLocation {
  latitude: number;
  longitude: number;
}

interface MockOrderMapMarkerProps {
  vehicleType: CourierVehicleType;
  courierName: string;
  location: MockOrderMapLocation;
  returnStatusType: CourierReturnStatusType;
  MockETADate?: Date;
  insertElement?: ReactNode;
}

const { xdsColorContentLight, xdsColorSupportPositive } = globalStyles;

export const MockCourierMapMarker = ({
  courierName,
  vehicleType,
  returnStatusType,
  courierId,
  onClick,
  etaTime,
  compareTime,
  dueTime,
  insertElement,
  location,
}: AdvancedMarkerProps & MockOrderMapMarkerProps) => {
  const getPinColorSchema = () => {
    const defaultCourierColorCombination = mapPinColorGenerator('#3C4C4F', xdsColorContentLight);
    const returningCourierColorCombination = mapPinColorGenerator(
      xdsColorSupportPositive,
      xdsColorContentLight
    );
    return returnStatusType !== CourierReturnStatusType.Returning
      ? defaultCourierColorCombination
      : returningCourierColorCombination;
  };

  const { t } = useTranslation();
  const { getCourierEtaById } = useDashboardComputedFunctions();
  const { getDistanceDuration } = useEtaBack();
  const { getCourierLocation } = useDashboardComputedFunctions();
  const locationData = getCourierLocation(courierId) as any;
  const { latitude = null, longitude = null } = location;
  const [etaTimeCompareValue, setEtaTimeCompareValue] = useState<string | false>(false);
  const { persistentOrderEtaListUpdateDate, setPersistentOrderEtaListUpdateDate } =
    useCommonPersistentStore();
  const getTagColor = () => {
    return returnStatusType !== CourierReturnStatusType.Returning
      ? ETagsColorType.Neutral
      : ETagsColorType.Success;
  };

  const howManyMinutesLeft = (baseTime: any, serverTime: any) => {
    return dayjs(serverTime).to(dayjs(baseTime), true);
  };
  const getCardContent = () => {
    let content = '';

    const minutesLeft = howManyMinutesLeft(etaTime || dueTime, etaTimeCompareValue);
    content =
      returnStatusType === CourierReturnStatusType.Returning
        ? getDistanceDuration(getCourierEtaById(courierId))
        : etaTime
        ? t('courier.delivering_in') + ` ${minutesLeft || ''}`
        : '';

    return (
      <>
        <PIEComponentUtil marginTop={!content ? '' : 'var(--xds-spacing-a)'}>
          <PIEText
            size="var(--xds-font-size-14)"
            fontWeight="var(--xds-font-weight-bold)"
            text={courierName}
            align="center"
          />
        </PIEComponentUtil>

        {content && (
          <PIETag mode={getTagColor()} className={styles.CourierMapMarkerDeliveredTag}>
            <PIEText
              size="var(--xds-font-size-14)"
              fontWeight="var(--xds-font-weight-bold)"
              text={content}
              align="center"
            />
          </PIETag>
        )}
      </>
    );
  };

  const isLocationDataReady = () => {
    return !!(latitude && longitude);
  };

  useRenderPhase(
    () => {
      //initial render
      if (persistentOrderEtaListUpdateDate) {
        setEtaTimeCompareValue(persistentOrderEtaListUpdateDate);
      } else {
        setEtaTimeCompareValue(compareTime as any);
      }
    },
    () => {
      //subsequent renders
      setPersistentOrderEtaListUpdateDate(compareTime as any);
      setEtaTimeCompareValue(compareTime as any);
    },
    [etaTime]
  );

  return (
    (isLocationDataReady() && (
      <>
        {insertElement && (
          <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
            {insertElement}
          </AdvancedMarker>
        )}
        <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
          <MapCard className={styles.mapMarkerCard}>{getCardContent()}</MapCard>
        </AdvancedMarker>
        <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
          <MapPin {...getPinColorSchema()}>
            <DashboardVehicleSelector vehicleType={vehicleType} />
          </MapPin>
        </AdvancedMarker>
      </>
    )) || <></>
  );
};

export default MockCourierMapMarker;
